import React, { useEffect, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  LayersControl,
  GeoJSON,
  Polyline,
  Marker,
  Pane,
  Tooltip,
  Popup,
  Circle
} from 'react-leaflet';
import { h3SetToFeature } from 'geojson2h3';
import Obstacles from './Obstacles';
import { RouteElement } from '../RouteElement';
import { useSelector } from 'react-redux';
import L, { Map } from 'leaflet';
import { indexPriority, geofenceColor } from '../../../utils/ui';
import markerIconPng from "leaflet/dist/images/marker-icon.png"
import { 
  AmberDropPointIcon, 
  CurrentDropPointIcon,
  CurrentDropPointIconRed,
  DroneMarker, 
  PreviousDropPointIcon, 
  SelectedPointMarker, 
  SuggestedPointMarker, 
  WaypointMarker,
  WaypointMarkerArrow
} from './MapIcons';
import { getOsmObstacleDataForCoordinates } from '../../../actions/obstacleActions';
import { FeatureCollection } from 'geojson';
import { DropLocation } from '@m4a3/api-client';
import { dropPointRadiusInMetres } from '../../../constants';
import RecentFlightsHeatmap from './RecentFlightsHeatmap';
import { selectLandingZones, selectSelectedCellId } from '../../../selectors/ordersSelector';
import { logToDatadog } from '../../../services/datadog';
import { RoutePoint, RoutePointType } from '../RouteConfirmation';

const getMarkerIcon = (
  isFirstCoord: boolean,
  isSelected: boolean,
  isDropPoint: boolean,
  isLastCoord: boolean,
  proposedNewDropPoint: any,
  rotationAngle: number,
  markerArrowsEnabled: boolean
) => {
  
  if (isDropPoint || isLastCoord) {

    if (!proposedNewDropPoint) {
      return CurrentDropPointIcon;
    }

    if (proposedNewDropPoint?.isLowRisk) {
      return CurrentDropPointIcon;
    } else if(proposedNewDropPoint?.isEvaluated && !proposedNewDropPoint?.isLowRisk) {
      return CurrentDropPointIconRed;
    } else {
      return AmberDropPointIcon;
    }
  } 

  if (isSelected) {
    return SelectedPointMarker;
  } else if (isFirstCoord || isLastCoord) {
    return DroneMarker;
  } 
  
  return markerArrowsEnabled ? WaypointMarkerArrow(rotationAngle) : WaypointMarker;
};

interface RoutePaneProps {
  missionRoutes: any;
  routeCoords: RoutePoint[];
  order: any;
  missionRoutesLen: any;
  selectedCoords: any;
  geofences: any;
  proposedNewDropPoint: any;
  removeMarker: (e: any, index: number) => void;
  updateMarker: (e: any, index: number) => void;
  getMarkerIcon: (isFirstCoord: boolean, isSelected: boolean, isDropPoint: boolean, isLastCoord: boolean, proposedNewDropPoint: any, rotationAngle: number, markerArrowsEnabled: boolean) => any;
  createProposedNewDropPoint: (lat: number, lng: number, isLowRisk: boolean, isEvaluated: boolean) => void;
  markerArrowsEnabled: boolean;
  isDragDisabled: boolean;
}

const routePaneComparison = (prevProps: RoutePaneProps, nextProps: RoutePaneProps) => {
  const result = (
    JSON.stringify(prevProps.routeCoords) === JSON.stringify(nextProps.routeCoords) &&
    JSON.stringify(prevProps.selectedCoords) === JSON.stringify(nextProps.selectedCoords) &&
    JSON.stringify(prevProps.missionRoutes) === JSON.stringify(nextProps.missionRoutes) &&
    JSON.stringify(prevProps.order) === JSON.stringify(nextProps.order) &&
    JSON.stringify(prevProps.geofences) === JSON.stringify(nextProps.geofences) &&
    JSON.stringify(prevProps.missionRoutesLen) === JSON.stringify(nextProps.missionRoutesLen) && 
    JSON.stringify(prevProps.proposedNewDropPoint) === JSON.stringify(nextProps.proposedNewDropPoint) &&
    prevProps.markerArrowsEnabled === nextProps.markerArrowsEnabled
  );

  return result;
};  

const RoutePane = React.memo<RoutePaneProps>(({
  missionRoutes,
  routeCoords,
  order,
  missionRoutesLen,
  selectedCoords,
  geofences,
  proposedNewDropPoint,
  removeMarker,
  updateMarker,
  getMarkerIcon,
  createProposedNewDropPoint,
  markerArrowsEnabled,
  isDragDisabled
}) => {

  const getAllMapMarkers = () => {
    if (!routeCoords) { return null }
  
    return routeCoords.map(routeCoord => {

      const routeCoordIndex = Number(routeCoord.index);
      const isLastCoord = routeCoords.length - 1 === routeCoordIndex;
      const isDropPoint = routeCoord.type === RoutePointType.CUSTOMER_DROP_LOCATION
      const isFirstCoord = routeCoordIndex === 0;
      const isSelected = !!selectedCoords.find(selectedCoord => {
        return (
          routeCoord.coordinates[0] === selectedCoord[0] && 
          routeCoord.coordinates[1] === selectedCoord[1]
        );
      });

      var angle = 0;
      if(!isFirstCoord && !isDropPoint && !isLastCoord) {
        var to = routeCoords[routeCoordIndex + 1];
        var from = routeCoord;
        var dy = to.coordinates[0] - from.coordinates[0];
        var dx = Math.cos(Math.PI / 180 * from.coordinates[0]) * (to.coordinates[1] - from.coordinates[1]);
        angle = ((Math.atan2(dy, dx) / Math.PI) * 180 * -1);
      }

      // Only disable dragging for drop points when isDragDisabled is true (cell 5)
      // Allow dragging of waypoints and other markers even in cell 5
      const shouldDisableDrag = isDragDisabled && isDropPoint;
  
      return (
        <Marker
          key={`routecoords-${routeCoord.index}`}
          opacity={isSelected ? 0.7 : 1}
          // @ts-ignore
          marker_index={routeCoordIndex}
          icon={getMarkerIcon(isFirstCoord, isSelected, isDropPoint, isLastCoord, proposedNewDropPoint, angle, markerArrowsEnabled)}
          position={routeCoord.coordinates}
          eventHandlers={{
            click: e => {
              if (order?.mission?.status === 'HOLD') { return; }
              removeMarker(e, routeCoordIndex);
            },
            dragend: async e => {
              // Only restrict drag operations for drop points in cell 5
              if (shouldDisableDrag) {
                return;
              }
              
              const { lat, lng } = e.target.getLatLng();
              updateMarker(e, routeCoordIndex);

              if (isLastCoord) {
                createProposedNewDropPoint(lat, lng, false, false);
              }
            }
          }}
          draggable={!shouldDisableDrag}
        />
      );
    });
  }

  const getOutboundRouteCoords = () => {
    const dropIndex = routeCoords.findIndex(coord => coord.type === RoutePointType.CUSTOMER_DROP_LOCATION);
    return routeCoords.slice(0, dropIndex + 1);
  }

  const getInboundRouteCoords = () => {
    const dropIndex = routeCoords.findIndex(coord => coord.type === RoutePointType.CUSTOMER_DROP_LOCATION);
    return routeCoords.slice(dropIndex);
  }

  return (
    <Pane name="RoutePane" style={{ zIndex: 10005 }}>
      <Pane name="GeoJsonPane" style={{ zIndex: 10004 }}>
        {missionRoutes &&
          Object.keys(missionRoutes)?.map(missionId => {
            return (
              <GeoJSON
                data={h3SetToFeature(missionRoutes[missionId])}
                style={{
                  color: 'none',
                  fillOpacity: 0.5,
                  fillColor: order.missionId === missionId ? 'yellow' : 'red'
                }}
                key={`${missionId}-${missionRoutesLen}`}
              />
            );
          })}
      {getAllMapMarkers()}
      </Pane>
      <Pane name="PolyRouteCoords" style={{ zIndex: 10003 }}>
        <Polyline
          positions={getOutboundRouteCoords().map(point => point.coordinates)}
          color="white"
          interactive={false}
          key={`${order.id}-routeCoords`}
        />
        <Polyline
          positions={getInboundRouteCoords().map(point => point.coordinates)}
          color="white"
          dashArray="10, 10"
          interactive={false}
          key={`${order.id}-routeCoords`}
        />
      </Pane>
      <Pane
        key="Geofences"
        name="Geofences"
        style={{ zIndex: indexPriority(9) }}>
        {geofences &&
          geofences.length > 0 &&
          geofences
            ?.filter(
              geofence =>
                // These are geofences active
                geofence.active === true
            )
            ?.map(geofence => {
              return (
                <GeoJSON
                  data={geofence.geoJson}
                  style={{ fill: false, color: geofenceColor(geofence.id) }}
                  key={geofence.id}
                />
              );
            })}
      </Pane>
    </Pane> 
  );
}, routePaneComparison);

const { BaseLayer } = LayersControl;

interface RouteConfirmationMapProps {
  landingPads: any;
  selectedOriginPad: any;
  selectedDestinationPad: any;
  intialCoords: any;
  outerMapBounds: any;
  mapRef: React.MutableRefObject<Map>;
  obstacles: any;
  missionRoutes: any;
  routeCoords: any;
  order: any;
  missionRoutesLen: any;
  selectedCoords: any;
  removeMarker: any;
  updateMarker: any;
  proposedNewDropPoint: any;
  currentCustomerDropPoint?: DropLocation;
  eircodeCoords: any;
  geoTunnel: any;
  dropPointHistory?: DropLocation[];
  geofences: any;
  shouldShowHeatmap: boolean;
  createProposedNewDropPoint: (lat: number, long: number, isLowRisk: boolean, isEvaluated: boolean) => void;
  markerArrowsEnabled: boolean;
  isDragDisabled: boolean;
}

export const RouteConfirmationMap = React.memo(({
  landingPads,
  selectedOriginPad,
  selectedDestinationPad,
  intialCoords,
  outerMapBounds,
  mapRef,
  obstacles,
  missionRoutes,
  routeCoords,
  order,
  missionRoutesLen,
  selectedCoords,
  removeMarker,
  updateMarker,
  proposedNewDropPoint,
  currentCustomerDropPoint,
  eircodeCoords,
  geoTunnel,
  dropPointHistory,
  geofences,
  shouldShowHeatmap,
  createProposedNewDropPoint,
  markerArrowsEnabled,
  isDragDisabled
}: RouteConfirmationMapProps) => {

  const selectedCellId = useSelector(selectSelectedCellId);
  const [currentOriginPad, setCurrentLandingPad] = useState(selectedOriginPad);
  const [currentDestinationPad, setCurrentDestinationPad] = useState(selectedDestinationPad);
  const [osmObstacleData, setOsmObstacleData] = useState<FeatureCollection>();
  const landingZones = useSelector(selectLandingZones);

  useEffect(() => {
    setCurrentLandingPad(selectedOriginPad);
  }, [selectedOriginPad]);

  useEffect(() => {
    setCurrentDestinationPad(selectedDestinationPad);
  }, [selectedDestinationPad]);

  useEffect(() => {
    if (eircodeCoords) {
      getOsmObstacleDataForCoordinates(eircodeCoords[0], eircodeCoords[1])
        .then(data => {
          setOsmObstacleData(data)
        });
    }
  }, [eircodeCoords]);

  

  const getOsmObstacles = React.useCallback(() => {

    if (!osmObstacleData) {
      return null;
    }

    const renderOsmObstacles = osmObstacleData?.features?.map((feature, index) => {

      if (feature.geometry.type?.toLowerCase() === 'point') {
        return <Marker
          key={`osm-point-${index}`}
          position={(feature as any).geometry.coordinates?.reverse()}
          icon={new L.Icon({
            iconUrl: markerIconPng,
            iconSize: [25, 41],

          })}>
          <Tooltip
            permanent
            direction="bottom"
            opacity={0.6}
            offset={[0, 10]}>
            {feature.properties.name || feature.properties.type}
          </Tooltip>
        </Marker>
        } else {
        return (
          <GeoJSON
            key={`osm-feature-${index}`}
            data={feature}
            style={{
              weight: 2,
              fillOpacity: 0.1,
              color: 'red'
          }}>
            <Popup>
              <div>
                <strong>Type:</strong> {feature.properties.type?.replace(/_/g, ' ')}<br/>
                {feature.properties.name && <>
                  <strong>Name:</strong> {feature.properties.name}<br/>
                </>}
              </div>
            </Popup>
          </GeoJSON>
          );
      }
    })

    return renderOsmObstacles;
  }, [osmObstacleData]);

  const getLandingZones = React.useCallback(() => {

    if (!landingZones) {
      return null;
    }

    const renderLandingZones = landingZones?.map(lz => {
      let parsedGeoJson;
      try {
        parsedGeoJson = JSON.parse(lz.geoJson);
      } catch (error) {
        logToDatadog('error_parsing_geodata', {landingZone: lz.id}, 'error');
        return null;
      }

      return (<Pane name={`landing-zone-${lz.id}`} key={`landing-zone-${lz.id}`}>
        <GeoJSON
          data={parsedGeoJson}
          style={{
            weight: 2,
            fillOpacity: 0.1,
            color: 'lightblue'
          }}>
        </GeoJSON>
      </Pane>)
    })

    return renderLandingZones;
  }, [landingZones]);

  const getLandingPads = () => {

    if (!landingPads) {
      return null;
    }

    const renderLandingPads = landingPads.filter(pad => pad?.id !== currentOriginPad?.id && pad?.id !== currentDestinationPad?.id)?.map((pad, index) => (
      <Pane
        style={{ zIndex: 9997 }}
        name={'pad ' + pad.id}
        key={`landing-pad-marker-${pad.id}`}>
        <Marker
          position={[pad.latitude, pad.longitude]}
          icon={PreviousDropPointIcon}></Marker>
      </Pane>
    ));

    return renderLandingPads;
  }

  const getDropPointHistory = () => {

    if (!dropPointHistory) {
      return null;
    }

    const renderDropHistory = dropPointHistory?.filter(dp => dp.id !== currentCustomerDropPoint?.id && Boolean(dp.suggestedForAddressFindId))?.map((dp) => {
      return (<Pane style={{ zIndex: 10005 }} name={'dph-' + dp.id} key={'dph-' + dp.id}>
        <Circle
          center={[dp?.latitude, dp?.longitude]}
          radius={dropPointRadiusInMetres / 2}
          fillOpacity={0.2}
          pathOptions={{color: '#899499'}}>
        </Circle>
      </Pane>);
    });

    return renderDropHistory;
  }

  const getDropPoint = () => {

    if (currentCustomerDropPoint?.latitude && currentCustomerDropPoint?.longitude) {

      return (
        <Pane style={{ zIndex: 10000 }} name={'currentCustomerDropPoint' + currentCustomerDropPoint?.id}>
          <Marker
          draggable={false}
          position={[currentCustomerDropPoint.latitude, currentCustomerDropPoint.longitude]}
          icon={PreviousDropPointIcon} 
          >
          <Tooltip permanent direction="bottom" opacity={1} offset={[0, 10]}>
            {'Customer'}
          </Tooltip>
        </Marker>             
        </Pane>
      )
    } 
    
    return null
  }

  const getEircodeCoords = () => {
    
    if (!eircodeCoords){
      return null;
    }
    
    return (
      <Pane style={{ zIndex: 10003, minWidth: '25px' }} name={'dph' + eircodeCoords.join(',')}>
        <Marker
          position={eircodeCoords}
          icon={new L.Icon({
            iconUrl: markerIconPng,
            iconSize: [25, 41],

          })}>
          <Tooltip
            permanent
            direction="bottom"
            opacity={0.6}
            offset={[0, 10]}>
            {'Eircode'}
          </Tooltip>
        </Marker>
      </Pane>
    );
  }

  return (
    <MapContainer
      style={{
        height: '100%',
        width: '100%',
        borderTopLeftRadius: 24,
        borderBottomLeftRadius: 24
      }}
      center={intialCoords}
      bounds={outerMapBounds}
      ref={mapRef}
      zoom={14}>
      <TileLayer
        attribution='&amp;copy <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <TileLayer
        attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
      />
      <LayersControl>
        <BaseLayer checked name="Manna v1">
          <TileLayer
            attribution="Manna-v1"
            url="https://tiles.manna.aero/{z}/{x}/{y}.png"
            maxZoom={24}
          />
        </BaseLayer>
        <BaseLayer name="Manna v2">
          <TileLayer
            attribution="Manna-v2"
            url="https://tiles-dev.manna.aero/{z}/{x}/{y}.png"
            maxZoom={24}
          />
        </BaseLayer>
      </LayersControl>

      <RecentFlightsHeatmap
        shouldShowHeatmap={shouldShowHeatmap}
        mapRef={mapRef}
        cellId={selectedCellId} />

      <Pane name="Obstacles" style={{ zIndex: 10005 }}>
        <Obstacles obstacles={obstacles} />
      </Pane>
      {getLandingZones()}
      {getOsmObstacles()}
      <Pane name="RouteElement" style={{ zIndex: 10000 }}>
        <RouteElement
          showStatuses={[
            'READY',
            'DISPATCHED',
            'DELIVERED',
            'RECEIVED_FROM_STORE'
          ]}
        />
      </Pane>
      <Pane name="polysuggested">
        {geoTunnel && (
          <GeoJSON
            data={geoTunnel}
            key={`${order.id}-geoTunnel`}
            style={{
              weight: 5,
              fillOpacity: 0.5,
              stroke: false,
              color: '#0583FF'
            }}
          />
        )}
      </Pane>
      <RoutePane
        missionRoutes={missionRoutes}
        routeCoords={routeCoords}
        order={order}
        missionRoutesLen={missionRoutesLen}
        selectedCoords={selectedCoords}
        geofences={geofences}
        proposedNewDropPoint={proposedNewDropPoint}
        removeMarker={(e: any, index: number) => {
          if (order?.mission?.status === 'HOLD') { return; }
          removeMarker(e, index);
        }}
        updateMarker={updateMarker}
        getMarkerIcon={getMarkerIcon}
        createProposedNewDropPoint={createProposedNewDropPoint}
        markerArrowsEnabled={markerArrowsEnabled}
        isDragDisabled={isDragDisabled}
      />
      {getLandingPads()}
      {getDropPointHistory()}
      {getDropPoint()}
      {(currentCustomerDropPoint &&
        order?.teamStatus !== 'DROP_SAFE' &&
        parseFloat(currentCustomerDropPoint?.[0]) &&
        parseFloat(currentCustomerDropPoint?.[1]) && (
          <Pane style={{ zIndex: 10008 }} name={'dphs' + currentCustomerDropPoint.id}>
            <Marker
              position={[parseFloat(currentCustomerDropPoint[0]), parseFloat(currentCustomerDropPoint[1])]}
              draggable={false}
              icon={SuggestedPointMarker}>
              <Tooltip
                permanent
                direction="bottom"
                opacity={1}
                offset={[0, 10]}>
                {'Suggested'}
              </Tooltip>
            </Marker>
          </Pane>
        )) ||
        null}
      {getEircodeCoords()}
    </MapContainer>
  );
})